import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play } from "../../api/types";
import { apiFetch } from "../../api/client";
import { AuthExpiredError } from "../../api/auth";
import { MAX_ZOOM, MIN_ZOOM, normalizeCrop, panCrop, PlayCrop } from "../../render/crop";
import { renderPlayPng } from "../../render/renderPlayPng";
import { BoxRect } from "../office/containRect";
import { SelectedChlkPlay, replaceChlkPlayImage } from "../office/chlkShape";
import { formatInsertError } from "../office/insertErrors";

interface Props {
  selection: SelectedChlkPlay;
  onDone: () => void;
  onAuthExpired: () => void;
}

const APPLY_DEBOUNCE_MS = 180;
const PREVIEW_DEBOUNCE_MS = 100;

export default function MaskPanel({ selection, onDone, onAuthExpired }: Props) {
  const [play, setPlay] = useState<Play | null>(null);
  const [crop, setCrop] = useState<PlayCrop>(() => normalizeCrop(selection.crop));
  const [preview, setPreview] = useState<string | null>(null);
  const [playName, setPlayName] = useState("");
  const [playWidth, setPlayWidth] = useState(0);
  const [playHeight, setPlayHeight] = useState(0);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; crop: PlayCrop } | null>(null);
  const cropRef = useRef(crop);
  cropRef.current = crop;
  const anchorRectRef = useRef<BoxRect>(selection.rect);
  const applyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    anchorRectRef.current = selection.rect;
    setCrop(normalizeCrop(selection.crop));

    apiFetch<Play>(`/play/${selection.playId}`)
      .then((fetched) => {
        if (cancelled) return;
        setPlay(fetched);
        setPlayName(fetched.name ?? "Play");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AuthExpiredError) {
          onAuthExpired();
        } else {
          setError("Could not load play.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selection.playId, selection.rect, selection.crop, onAuthExpired]);

  const renderPreview = useCallback(async (nextCrop: PlayCrop, currentPlay: Play, generation: number) => {
    const rendered = await renderPlayPng(currentPlay, {
      crop: nextCrop,
      scale: Math.min(2 * nextCrop.zoom, 4),
      skipFetch: true,
    });
    if (generation !== previewGeneration.current) return rendered;
    setPlayWidth(rendered.playWidth);
    setPlayHeight(rendered.playHeight);
    setPreview(rendered.png);
    return rendered;
  }, []);

  const commitApply = useCallback(
    async (nextCrop: PlayCrop, currentPlay: Play, generation: number) => {
      setApplying(true);
      setError(null);
      try {
        const rendered = await renderPlayPng(currentPlay, { crop: nextCrop, skipFetch: true });
        if (generation !== applyGeneration.current) return;

        const newRect = await replaceChlkPlayImage(
          selection.playId,
          rendered.png,
          rendered.width,
          rendered.height,
          nextCrop,
          anchorRectRef.current
        );
        if (generation !== applyGeneration.current) return;

        anchorRectRef.current = newRect;
        setPreview(rendered.png);
        setPlayWidth(rendered.playWidth);
        setPlayHeight(rendered.playHeight);
      } catch (err) {
        if (generation !== applyGeneration.current) return;
        console.error("[CHLK mask apply]", err);
        if (err instanceof AuthExpiredError) {
          onAuthExpired();
        } else {
          setError(formatInsertError(err));
        }
      } finally {
        if (generation === applyGeneration.current) {
          setApplying(false);
        }
      }
    },
    [selection.playId, onAuthExpired]
  );

  const scheduleApply = useCallback(
    (nextCrop: PlayCrop) => {
      if (!play) return;
      applyGeneration.current += 1;
      if (applyTimer.current) clearTimeout(applyTimer.current);
      const generation = applyGeneration.current;
      applyTimer.current = setTimeout(() => {
        void commitApply(nextCrop, play, generation);
      }, APPLY_DEBOUNCE_MS);
    },
    [play, commitApply]
  );

  useEffect(() => {
    if (!play) return;

    previewGeneration.current += 1;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const generation = previewGeneration.current;

    previewTimer.current = setTimeout(() => {
      renderPreview(crop, play, generation).catch((err) =>
        console.error("[CHLK mask preview]", err)
      );
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [play, crop, renderPreview]);

  useEffect(() => {
    return () => {
      if (applyTimer.current) clearTimeout(applyTimer.current);
      if (previewTimer.current) clearTimeout(previewTimer.current);
      applyGeneration.current += 1;
      previewGeneration.current += 1;
    };
  }, []);

  function handleZoomChange(value: number) {
    const next = normalizeCrop({ ...crop, zoom: value });
    setCrop(next);
    scheduleApply(next);
  }

  function handlePointerDown(e: React.PointerEvent) {
    if (!playWidth || !playHeight) return;
    previewRef.current?.setPointerCapture(e.pointerId);
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, crop };
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!dragging || !dragStart.current || !playWidth || !playHeight) return;
    const el = previewRef.current;
    if (!el) return;

    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    const startCrop = dragStart.current.crop;
    const normalized = panCrop(
      startCrop,
      -(deltaX / el.clientWidth) * (1 / startCrop.zoom),
      -(deltaY / el.clientHeight) * (1 / startCrop.zoom),
      playWidth,
      playHeight
    );
    setCrop(normalized);
  }

  function handlePointerUp(e: React.PointerEvent) {
    if (!dragging) return;
    previewRef.current?.releasePointerCapture(e.pointerId);
    setDragging(false);
    dragStart.current = null;
    if (play) {
      scheduleApply(cropRef.current);
    }
  }

  if (loading) {
    return <div className="mask-panel loading">Loading play…</div>;
  }

  if (error && !play) {
    return (
      <div className="mask-panel">
        <div className="mask-error">{error}</div>
        <button className="btn-mask-done" onClick={onDone}>Back</button>
      </div>
    );
  }

  return (
    <div className="mask-panel">
      <div className="mask-header">
        <div>
          <div className="mask-title">Zoom Play</div>
          <div className="mask-subtitle">{playName}</div>
        </div>
        <button className="btn-mask-done" onClick={onDone} disabled={applying}>
          Done
        </button>
      </div>

      <div
        ref={previewRef}
        className={`mask-preview${dragging ? " dragging" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {preview ? (
          <img src={`data:image/png;base64,${preview}`} alt={playName} draggable={false} />
        ) : (
          <div className="mask-preview-placeholder" />
        )}
        <div className="mask-preview-hint">Drag to pan</div>
      </div>

      <div className="mask-controls">
        <label className="mask-zoom-label">
          <span>Zoom</span>
          <span className="mask-zoom-value">{crop.zoom.toFixed(1)}×</span>
        </label>
        <input
          type="range"
          className="mask-zoom-slider"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.1}
          value={crop.zoom}
          onChange={(e) => handleZoomChange(Number(e.target.value))}
          disabled={applying}
        />
      </div>

      {error && <div className="mask-error">{error}</div>}
      {applying && <div className="mask-status">Updating slide…</div>}
    </div>
  );
}
